import React from 'react';
import styled from 'styled-components';
import {
  Button,
  BodyText,
  CheckmarkIcon,
  Title,
  Spacer
} from '@kvdbil/components';
import Auction from '~/App/shared/interfaces/Auction';
import { useTranslation } from '~/Locale';
import { useSelector } from 'react-redux';
import { scroller } from 'react-scroll';
import SaveSearchDialog from './SaveSearchDialog';
import { savedSearch } from '~/App/shared/selectors/auctionSearchSelector';
import { getLocalFilters } from '~/helpers/auctionSearch';
import BuyDirect from './BuyDirect';
import { isRecreationalVehicle as isRecreationalVehicleHelper } from '~/helpers/auction';
import { BuyMethodContainer } from './BuyMethodBox';

const SavedSearchButton = styled(Button)`
  display: flex;
  justify-content: center;
  align-items: center;
`;

const ButtonContainer = styled.div`
  margin-top: auto;
`;

interface Props {
  auction: Auction;
  hasSimilarObjects?: boolean;
}

const SimilarCarsBox = ({ auction, hasSimilarObjects }: Props) => {
  const { t } = useTranslation();
  const [modalOpen, setModalOpen] = React.useState(false);
  const { isSoldByBuyNow, isReserved } = auction;

  const filters = getLocalFilters(auction);
  const isSearchSaved = useSelector(savedSearch(filters)) !== undefined;
  const isRecreationalVehicle = isRecreationalVehicleHelper(
    auction?.processObject?.objectType ?? ''
  );

  const handleClose = () => setModalOpen(false);

  const scrollTo = (id: string) => {
    scroller.scrollTo(id, {
      duration: 800,
      smooth: true,
      dynamic: true,
      offset: 0
    });
  };

  const openSavedSearchModal = () => {
    setModalOpen(true);
  };

  const renderTitle = () => {
    if (isSoldByBuyNow) {
      return t('Sold via fixed price');
    } else if (isReserved) {
      return t('To be sold via fixed price');
    } else {
      return t('Find similar');
    }
  };

  return (
    <BuyMethodContainer>
      <Title as="h3">{renderTitle()}</Title>

      {(isSoldByBuyNow || isReserved) && (
        <>
          <Spacer />

          <BuyDirect
            auction={auction}
            isRecreationalVehicle={isRecreationalVehicle}
            priceText={t('Sold for')}
          />
        </>
      )}

      <Spacer />

      <BodyText>
        {isReserved
          ? t('SOON_SOLD_DESCRIPTION')
          : t('FIND_SIMILAR_CAR_DESCRIPTION')}
      </BodyText>

      <Spacer height={2} />

      <ButtonContainer>
        <SavedSearchButton
          fullWidth
          size="large"
          color="info"
          colorShade="dark"
          variant={isSearchSaved ? 'outline' : 'solid'}
          onClick={openSavedSearchModal}
          prefixIcon={isSearchSaved ? <CheckmarkIcon /> : null}
        >
          {isSearchSaved ? t('Search watched') : t('Create a saved search')}
        </SavedSearchButton>

        {hasSimilarObjects && (
          <>
            <Spacer height={2} />

            <Button
              variant="outline"
              color="info"
              colorShade="dark"
              onClick={() => scrollTo('similar-auctions-section')}
              as="a"
              size="large"
            >
              {t('See similar cars')}
            </Button>
          </>
        )}
      </ButtonContainer>

      {modalOpen && <SaveSearchDialog handleClose={handleClose} />}
    </BuyMethodContainer>
  );
};

export default SimilarCarsBox;
